/** API client for the floating info-assistant widget (`/assistant/*`). */

import axios from 'axios';
import { VIBE_CHAT_API_BASE } from '@/lib/vibeChatConfig';
import { formatApiError } from '@/lib/vibeChatApi';

export { formatApiError };

const ACTIVE_SESSION_STORAGE_KEY = 'sap-notes-assistant.activeSessionId';

export function readActiveAssistantSessionId() {
  try {
    return String(sessionStorage.getItem(ACTIVE_SESSION_STORAGE_KEY) || '').trim();
  } catch {
    return '';
  }
}

export function persistActiveAssistantSessionId(sessionId) {
  const id = String(sessionId || '').trim();
  try {
    if (id) {
      sessionStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, id);
    } else {
      sessionStorage.removeItem(ACTIVE_SESSION_STORAGE_KEY);
    }
  } catch {
    /* private mode / quota */
  }
}

/**
 * Kicks off a turn. The backend returns immediately (status: "processing") —
 * the actual SAP Notes search/synthesis can take 60-90s, so the answer is
 * fetched separately via pollAssistantAnswer / fetchAssistantSessionMessages.
 */
export async function sendSapNotesMessage({ sessionId, message, lang = 'EN' }) {
  const { data } = await axios.post(`${VIBE_CHAT_API_BASE}/assistant/chat`, {
    session_id: sessionId || null,
    message,
    lang,
  });
  return data;
}

/**
 * Wipes stored data for this session (called on logout and on panel-close).
 * Best-effort: failures are swallowed by the caller, not here, so a delete
 * hiccup never blocks logout or closing the panel.
 */
export async function deleteAssistantSession(sessionId) {
  const id = String(sessionId || '').trim();
  if (!id) return;
  await axios.delete(`${VIBE_CHAT_API_BASE}/assistant/sessions/${id}`);
}

export async function fetchAssistantSessionMessages(sessionId) {
  const id = String(sessionId || '').trim();
  if (!id) return { session_id: '', status: 'idle', messages: [], latest: null };
  const { data } = await axios.get(`${VIBE_CHAT_API_BASE}/assistant/sessions/${id}/messages`);
  return data;
}

/**
 * Polls GET .../messages until status is "done"/"failed", or timeoutMs elapses.
 * Returns the final poll response; on timeout, `status` is "processing" still
 * (caller should treat that as "still working, taking longer than usual").
 */
export async function pollAssistantAnswer(sessionId, { intervalMs = 2000, timeoutMs = 120000 } = {}) {
  const deadline = Date.now() + timeoutMs;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const data = await fetchAssistantSessionMessages(sessionId);
    if (data.status === 'done' || data.status === 'failed') {
      return data;
    }
    if (Date.now() >= deadline) {
      return data;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}
